import db from '@/firebase/init'

const state = () => ({
  items: [],
  loading: false,
  error: null,
  _unsub: null,
})

const getters = {
  all: (s) => s.items,
  count: (s) => s.items.length,
  loading: (s) => s.loading,
  error: (s) => s.error,
}

const mutations = {
  SET_ITEMS(s, items) {
    s.items = items
  },
  SET_LOADING(s, v) {
    s.loading = v
  },
  SET_ERROR(s, e) {
    s.error = e ? String(e) : null
  },
  SET_UNSUB(s, fn) {
    s._unsub = fn
  },
  CLEAR(s) {
    s.items = []
    s.error = null
  },
  RESET(s) {
    if (s._unsub) s._unsub()
    s.items = []
    s.loading = false
    s.error = null
    s._unsub = null
  },
}

const actions = {
  async startListening({ state, commit }, { courseID, unitID, sectionID }) {
    if (state._unsub) {
      state._unsub()
      commit('SET_UNSUB', null)
    }
    if (!courseID || !unitID || !sectionID) {
      commit('SET_ITEMS', [])
      return
    }

    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const q = db
        .collection('coursecontent')
        .where('courseID', '==', courseID)
        .where('unitID', '==', unitID)
        .where('sectionID', '==', sectionID)

      const unsub = q.onSnapshot(
        (snap) => {
          const items = snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            // keep only paragraph highlight docs (not comments, not notes)
            .filter(
              (doc) => typeof doc.paragraph === 'string' && doc.paragraphID
            )
            // expand paragraph into per-span items
            .flatMap((doc) => {
              const tmp = document.createElement('div')
              tmp.innerHTML = doc.paragraph || ''
              const spans = tmp.querySelectorAll('.highlighted')

              // Detect parent section from paragraph wrapper
              let section = 'other'
              const pEl = document.createElement('div')
              pEl.innerHTML = doc.paragraph
              const para = pEl.querySelector(`[id="${doc.paragraphID}"]`)
              if (para) {
                const container = para.closest('.p-content-container')
                if (container?.id) {
                  section = container.id
                }
              }

              return Array.from(spans).map((span, idx) => {
                // prefer solid base color from the data-attr
                let dotColor = span.getAttribute('data-highlight-color')
                if (!dotColor) {
                  // fallback: if style is rgba(..., a), drop alpha to make a solid rgb
                  const styleBg =
                    (span.style && span.style.backgroundColor) || ''
                  const m = styleBg.match(
                    /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)$/i
                  )
                  dotColor = m ? `rgb(${m[1]}, ${m[2]}, ${m[3]})` : '#ffeb3b'
                }

                return {
                  id: `${doc.id}:${idx}`,
                  docId: doc.id,
                  index: idx,
                  paragraphID: doc.paragraphID,
                  excerpt: (span.textContent || '').trim(),
                  paragraph: doc.paragraph, // keep for search fallback
                  bg: dotColor, // solid color for the panel dot
                  section,
                }
              })
            })

          items.sort((a, b) => {
            const ap = String(a.paragraphID || '')
            const bp = String(b.paragraphID || '')
            if (ap === bp) return a.index - b.index
            return ap.localeCompare(bp)
          })

          commit('SET_ITEMS', items)
          commit('SET_LOADING', false)
        },
        (err) => {
          commit('SET_ERROR', err)
          commit('SET_LOADING', false)
        }
      )
      commit('SET_UNSUB', unsub)
    } catch (e) {
      commit('SET_ERROR', e)
      commit('SET_LOADING', false)
    }
  },

  stopListening({ state, commit }) {
    if (state._unsub) {
      state._unsub()
      commit('SET_UNSUB', null)
    }
    commit('CLEAR')
  },

  async remove(_, id) {
    if (!id) return
    await db.collection('coursecontent').doc(id).delete()
  },
}

export default { namespaced: true, state, getters, mutations, actions }
